import { create } from 'zustand';
import { useDataStore } from '@/store/useDataStore';
import { useConfigStore } from '@/store/useConfigStore';
import { isLocalOCRInstance, selectOCRInstance } from '@/services/ocrService';
import { logInfo, logWarn, logError, logSuccess } from '@/store/useLogStore';
import type { ProgressInfo, DataRow, StepProductResult, StepTaskResult, LLMInstance } from '@/types';
import { EXCEL_FIELD_LABELS } from '@/lib/constants';
import {
    buildSkuProductRequest,
    buildSkuProductsBatchRequest,
    buildSkuRows,
    buildSkuWeightProductRequest,
    buildSkuWeightProductsBatchRequest,
    buildTitleBatchRequest,
    parseSkuProductResponse,
    parseSkuProductsBatchResponse,
    parseSkuWeightProductResponse,
    parseSkuWeightProductsBatchResponse,
    parseTitleBatchResponse,
    type SkuWeightBatchItem,
} from '@/services/batchSubmission';

// 产品级公共字段（同产品的不同 SKU 这些字段相同，批量处理时只需处理一次）
const PRODUCT_COMMON_IMAGE_FIELDS = ['detailImages', 'mainImages'];

// 全局批量操作进度状态，跨页面持久化
interface BatchTask {
    id: string;
    label: string;
    progress: ProgressInfo;
    running: boolean;
    abortController: AbortController | null;
}

// 步进确认模式状态
interface StepConfirmState {
    active: boolean;
    completedResults: StepProductResult[]; // 所有已处理完的产品结果
    currentProductIndex: number;
    totalProducts: number;
    processing: boolean; // 正在处理产品（非等待用户确认）
    cancelled: boolean;
}

interface BatchState {
    tasks: Map<string, BatchTask>;
    stepState: StepConfirmState;
    startTask: (label: string, total: number, message?: string) => { id: string; signal: AbortSignal };
    finishTask: (id: string, progress: ProgressInfo) => void;
    runBatch: (type: 'title' | 'filter' | 'size' | 'sku-spec' | 'sku-weight', selectedRowIndices: number[], useAllIfEmpty: boolean) => Promise<void>;
    runStepBatch: (types: ('title' | 'filter' | 'size' | 'sku-spec' | 'sku-weight')[], selectedRowIndices: number[], useAllIfEmpty: boolean) => Promise<void>;
    confirmStepContinue: () => void;
    cancelStepRemaining: () => void;
    retryStepTask: (taskType: 'title' | 'filter' | 'size' | 'sku-spec' | 'sku-weight', productId: string) => Promise<void>;
    cancelTask: (id: string) => void;
    dismissTask: (id: string) => void;
    updateProgress: (id: string, progress: ProgressInfo, running: boolean) => void;
}

export type BatchTaskType = 'title' | 'filter' | 'size' | 'aiImage' | 'sku-spec' | 'sku-weight';

interface BatchLifecycleHandler {
    shouldSkip?: (taskType: BatchTaskType, productId: string) => boolean;
    onProductCompleted?: (taskType: BatchTaskType, productId: string) => void | Promise<void>;
}

let batchLifecycleHandler: BatchLifecycleHandler | null = null;

export function setBatchLifecycleHandler(handler: BatchLifecycleHandler | null): void {
    batchLifecycleHandler = handler;
}

export function shouldSkipBatchProduct(taskType: BatchTaskType, productId: string): boolean {
    return batchLifecycleHandler?.shouldSkip?.(taskType, productId) ?? false;
}

export async function markBatchProductCompleted(taskType: BatchTaskType, productId: string): Promise<void> {
    await batchLifecycleHandler?.onProductCompleted?.(taskType, productId);
}

function configuredBatchSize(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(1, Math.min(100, Math.floor(parsed))) : 10;
}

// 步进确认的 Promise 解析函数
let _stepContinueResolve: (() => void) | null = null;
let _stepCancelResolve: (() => void) | null = null;

export const useBatchStore = create<BatchState>((set, get) => ({
    tasks: new Map(),
    stepState: { active: false, completedResults: [], currentProductIndex: 0, totalProducts: 0, processing: false, cancelled: false },

    startTask: (label, total, message = '准备中...') => {
        const id = `external-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        const abortController = new AbortController();
        set((state) => {
            const next = new Map(state.tasks);
            next.set(id, { id, label, progress: { current: 0, total, message }, running: true, abortController });
            return { tasks: next };
        });
        return { id, signal: abortController.signal };
    },

    finishTask: (id, progress) => {
        get().updateProgress(id, progress, false);
        setTimeout(() => get().dismissTask(id), 5000);
    },

    updateProgress: (id, progress, running) => {
        set((state) => {
            const next = new Map(state.tasks);
            const existing = next.get(id);
            if (existing) {
                next.set(id, { ...existing, progress, running });
            }
            return { tasks: next };
        });
    },

    runBatch: async (type, selectedRowIndices, useAllIfEmpty) => {
        const store = useDataStore.getState();
        const config = useConfigStore.getState();
        const allIndices = selectedRowIndices.length > 0
            ? selectedRowIndices
            : (useAllIfEmpty ? store.rows.map((_, i) => i) : []);
        if (allIndices.length === 0) return;

        const taskId = `${type}-${Date.now()}`;
        const labels: Record<string, string> = { title: '优化标题', filter: '过滤低质量', size: '识别尺码图', 'sku-spec': 'SKU规格优化', 'sku-weight': 'SKU估重' };
        const abortController = new AbortController();

        set((state) => {
            const next = new Map(state.tasks);
            next.set(taskId, { id: taskId, label: labels[type], progress: { current: 0, total: 0, message: '准备中...' }, running: true, abortController });
            return { tasks: next };
        });

        logInfo(labels[type], `开始批量任务，共 ${allIndices.length} 行 / ${groupByProductId(allIndices, store.rows).length} 个产品`);

        const onProgress = (p: ProgressInfo) => get().updateProgress(taskId, p, p.current < p.total || p.message.includes('准备中'));

        try {
            // 按 productId 分组
            const groups = groupByProductId(allIndices, store.rows)
                .filter(group => !batchLifecycleHandler?.shouldSkip?.(type, group.productId));
            const total = groups.length;
            const usesBatchSubmission = (type === 'title' && config.titleConfig.submissionMode !== 'single')
                || (type === 'sku-spec' && config.skuConfig.specSubmissionMode !== 'single')
                || (type === 'sku-weight' && config.skuConfig.weightSubmissionMode !== 'single');
            if (usesBatchSubmission) {
                if (type === 'title') await runTitleSubmissionBatches(groups, config, store, abortController.signal, onProgress);
                else if (type === 'sku-spec') await runSkuSpecSubmissionBatches(groups, config, store, abortController.signal, onProgress);
                else await runSkuWeightSubmissionBatches(groups, config, store, abortController.signal, onProgress);
                return;
            }
            for (let i = 0; i < total; i++) {
                if (abortController.signal.aborted) break;
                const group = groups[i];
                onProgress({ current: i + 1, total, message: `正在处理第 ${i + 1}/${total} 个产品...` });
                if (type === 'title') await executeTitleForProduct(group, abortController.signal);
                else if (type === 'filter') await executeFilterForProduct(group, abortController.signal);
                else if (type === 'size') await executeSizeForProduct(group, abortController.signal);
                else if (type === 'sku-spec') await executeSkuSpecForProduct(group, abortController.signal);
                else await executeSkuWeightForProduct(group, abortController.signal);
                if (abortController.signal.aborted) break;
                await batchLifecycleHandler?.onProductCompleted?.(type, group.productId);
                if (i < total - 1) {
                    const intervalSeconds = type === 'title'
                        ? (config.titleConfig.requestInterval ?? 10)
                        : type === 'filter' || type === 'size'
                            ? (config.ocrSettings.requestInterval ?? 10)
                            : type === 'sku-spec'
                                ? (config.skuConfig.specRequestInterval ?? 10)
                                : (config.skuConfig.weightRequestInterval ?? 10);
                    await new Promise(resolve => setTimeout(resolve, intervalSeconds * 1000));
                }
            }
            onProgress({ current: total, total, message: abortController.signal.aborted ? '已暂停' : `完成：已处理 ${total} 个产品` });
        } finally {
            const progress = get().tasks.get(taskId)?.progress ?? { current: 0, total: 0, message: '' };
            get().updateProgress(taskId, progress, false);
            // Auto-dismiss completed task after 5 seconds
            setTimeout(() => get().dismissTask(taskId), 5000);
        }
    },

    runStepBatch: async (types, selectedRowIndices, useAllIfEmpty) => {
        const store = useDataStore.getState();
        const allIndices = selectedRowIndices.length > 0
            ? selectedRowIndices
            : (useAllIfEmpty ? store.rows.map((_, i) => i) : []);
        if (allIndices.length === 0) return;

        // types 已由页面上的任务选择明确给出；不要再用配置页 enabled 二次过滤，
        // 否则同一个任务在普通模式可执行、步进模式却会被静默跳过。
        const enabledTypes = types;
        if (enabledTypes.length === 0) return;

        const groups = groupByProductId(allIndices, store.rows)
            .filter(group => enabledTypes.some(type => !batchLifecycleHandler?.shouldSkip?.(type, group.productId)));
        const total = groups.length;

        const labels: Record<string, string> = { title: '优化标题', filter: '过滤低质量', size: '识别尺码图', 'sku-spec': 'SKU规格优化', 'sku-weight': 'SKU估重' };
        const abortController = new AbortController();

        // 设置步进状态
        set({
            stepState: { active: true, completedResults: [], currentProductIndex: 0, totalProducts: total, processing: true, cancelled: false },
        });

        const taskId = `step-${Date.now()}`;
        set((state) => {
            const next = new Map(state.tasks);
            next.set(taskId, { id: taskId, label: enabledTypes.map(t => labels[t]).join(' + '), progress: { current: 0, total, message: '步进模式准备中...' }, running: true, abortController });
            return { tasks: next };
        });

        logInfo('步进模式', `开始步进批量任务，共 ${total} 个产品，任务: ${enabledTypes.map(t => labels[t]).join(', ')}`);

        const completedResults: StepProductResult[] = [];
        const batchSize = useConfigStore.getState().stepBatchSize || 1;

        for (let i = 0; i < total; i++) {
            if (abortController.signal.aborted) break;

            const group = groups[i];
            const productStart = Date.now();

            // 更新步进进度
            set((state) => ({
                stepState: { ...state.stepState, currentProductIndex: i, processing: true },
            }));
            get().updateProgress(taskId, { current: i + 1, total, message: `正在处理第 ${i + 1}/${total} 个产品...` }, true);

            // 快照：处理前该产品所有行数据（每次循环都获取最新状态）
            const snapshotBefore: DataRow[] = group.indices.map(idx => ({ ...useDataStore.getState().rows[idx] }));

            // 逐一执行每个启用的任务
            const taskResults: StepTaskResult[] = [];
            for (const taskType of enabledTypes) {
                if (abortController.signal.aborted) break;
                if (batchLifecycleHandler?.shouldSkip?.(taskType, group.productId)) continue;
                const taskStart = Date.now();
                let result: StepTaskResult = { taskType, status: 'failed', message: '不支持的任务类型', durationMs: 0 };
                try {
                    if (taskType === 'title') {
                        result = await executeTitleForProduct(group, abortController.signal);
                    } else if (taskType === 'filter') {
                        result = await executeFilterForProduct(group, abortController.signal);
                    } else if (taskType === 'size') {
                        result = await executeSizeForProduct(group, abortController.signal);
                    } else if (taskType === 'sku-spec') {
                        result = await executeSkuSpecForProduct(group, abortController.signal);
                    } else if (taskType === 'sku-weight') {
                        result = await executeSkuWeightForProduct(group, abortController.signal);
                    }
                } catch (err) {
                    result = { taskType, status: 'failed', message: err instanceof Error ? err.message : String(err), durationMs: Date.now() - taskStart };
                }
                taskResults.push(result);
                if (!abortController.signal.aborted) {
                    await batchLifecycleHandler?.onProductCompleted?.(taskType, group.productId);
                }
            }

            const totalDurationMs = Date.now() - productStart;

            // 快照：处理后该产品所有行数据（重新获取最新状态，确保反映任务执行结果）
            const snapshotAfter: DataRow[] = group.indices.map(idx => ({ ...useDataStore.getState().rows[idx] }));

            const productResult: StepProductResult = {
                productId: group.productId,
                productIndex: i,
                totalProducts: total,
                taskResults,
                totalDurationMs,
                snapshotBefore,
                snapshotAfter,
                originalIndices: group.indices,
            };

            completedResults.push(productResult);

            // 实时更新已完成的结果
            set((state) => ({
                stepState: { ...state.stepState, completedResults: [...completedResults] },
            }));

            // 每处理完 batchSize 个产品，暂停等用户确认（最后一批也在循环结束后统一处理）
            if ((i + 1) % batchSize === 0 && i + 1 < total) {
                // 暂停，等用户确认
                set((state) => ({
                    stepState: { ...state.stepState, processing: false },
                }));
                get().updateProgress(taskId, { current: i + 1, total, message: `已处理 ${i + 1}/${total} 个产品，等待确认...` }, false);

                const batchAction = await new Promise<'continue' | 'cancel'>((resolve) => {
                    _stepContinueResolve = () => resolve('continue');
                    _stepCancelResolve = () => resolve('cancel');
                });

                if (batchAction === 'cancel') {
                    // 撤销所有已处理产品的修改：恢复为 originalRows
                    const store = useDataStore.getState();
                    const { rows, originalRows } = store;
                    for (let j = 0; j < rows.length && j < originalRows.length; j++) {
                        if (JSON.stringify(rows[j]) !== JSON.stringify(originalRows[j])) {
                            store.updateRow(j, { ...originalRows[j] });
                        }
                    }
                    store.clearImageReplacements();
                    set((state) => ({ stepState: { ...state.stepState, cancelled: true, active: false } }));
                    break;
                }

                // 清除已完成结果，准备下一批
                completedResults.length = 0;
                set((state) => ({
                    stepState: { ...state.stepState, completedResults: [], processing: true },
                }));
            }
        }

        if (get().stepState.cancelled) {
            const cancelledProgress = get().tasks.get(taskId)?.progress ?? { current: 0, total, message: '已撤销' };
            get().updateProgress(taskId, { ...cancelledProgress, message: '已撤销所有修改，步进任务结束' }, false);
            setTimeout(() => get().dismissTask(taskId), 3000);
            return;
        }

        // 所有产品处理完毕，等待用户确认
        // 显式确保 stepState 满足 modal 显示条件
        set((state) => ({
            stepState: { ...state.stepState, processing: false, active: true, cancelled: false },
        }));
        get().updateProgress(taskId, { current: total, total, message: `全部 ${total} 个产品已处理完毕，等待确认...` }, false);

        // 如果没有结果（被 cancel 中断），直接结束
        if (completedResults.length === 0 || get().stepState.cancelled) {
            set((state) => ({ stepState: { ...state.stepState, active: false } }));
            return;
        }

        // 等待用户确认或取消
        const userAction = await new Promise<'continue' | 'cancel'>((resolve) => {
            _stepContinueResolve = () => resolve('continue');
            _stepCancelResolve = () => resolve('cancel');
        });

        if (userAction === 'cancel') {
            // 撤销所有已处理产品的修改：恢复为 originalRows
            const store = useDataStore.getState();
            const { rows, originalRows } = store;
            for (let j = 0; j < rows.length && j < originalRows.length; j++) {
                if (JSON.stringify(rows[j]) !== JSON.stringify(originalRows[j])) {
                    store.updateRow(j, { ...originalRows[j] });
                }
            }
            store.clearImageReplacements();
            set((state) => ({ stepState: { ...state.stepState, cancelled: true, active: false } }));
        }

        // 步进完成
        const finalProgress = get().tasks.get(taskId)?.progress ?? { current: total, total, message: '步进模式完成' };
        get().updateProgress(taskId, { ...finalProgress, message: '步进模式完成' }, false);

        set((state) => ({
            stepState: { ...state.stepState, active: false, processing: false },
        }));

        setTimeout(() => get().dismissTask(taskId), 3000);
    },

    confirmStepContinue: () => {
        if (_stepContinueResolve) {
            // 先设置 processing: true 让弹窗立即关闭
            set((state) => ({
                stepState: { ...state.stepState, processing: true },
            }));
            _stepContinueResolve();
            _stepContinueResolve = null;
            _stepCancelResolve = null;
        } else {
            // 如果没有等待的 Promise（例如仅仅是展示结果），直接关闭
            set((state) => ({
                stepState: { ...state.stepState, active: false, processing: false },
            }));
        }
    },

    cancelStepRemaining: () => {
        // 撤销所有步进修改：将所有被修改的行恢复为 originalRows 的值
        const store = useDataStore.getState();
        const { rows, originalRows } = store;
        for (let i = 0; i < rows.length && i < originalRows.length; i++) {
            if (JSON.stringify(rows[i]) !== JSON.stringify(originalRows[i])) {
                store.updateRow(i, { ...originalRows[i] });
            }
        }
        // 清除 imageReplacements
        store.clearImageReplacements();

        // 结束步进状态
        set((state) => ({
            stepState: { ...state.stepState, cancelled: true, active: false, completedResults: [] },
        }));

        if (_stepCancelResolve) {
            _stepCancelResolve();
            _stepCancelResolve = null;
            _stepContinueResolve = null;
        }
    },

    retryStepTask: async (taskType, productId) => {
        const { stepState } = get();
        const result = stepState.completedResults.find(r => r.productId === productId);
        if (!result) return;

        const store = useDataStore.getState();
        const rows = store.rows;
        // 找到该产品的行索引
        const group = findProductGroup(result.productId, rows);
        if (!group) return;

        const taskStart = Date.now();
        const abortController = new AbortController();

        // 恢复原始值后再重新执行
        const originalRow = store.originalRows[group.firstIndex];
        if (originalRow) {
            if (taskType === 'title') {
                // 恢复 productName
                store.updateCell(group.firstIndex, 'productName', originalRow.productName ?? '');
            } else if (taskType === 'filter') {
                // 恢复图片字段
                const filterFields = ['detailImages', 'mainImages', 'skuImage'];
                for (const f of filterFields) {
                    if (originalRow[f] !== undefined) {
                        applyToGroup(group, { [f]: originalRow[f] }, store);
                    }
                }
                // 取消过滤标记
                store.unmarkRowsFiltered(group.indices);
            } else if (taskType === 'size') {
                // 恢复 sizeChart
                applyToGroup(group, { sizeChart: originalRow.sizeChart ?? '' }, store);
            } else if (taskType === 'sku-spec') {
                // 恢复 sku 规格字段（按行）
                const fields = useConfigStore.getState().skuConfig.specFields ?? [];
                for (const idx of group.indices) {
                    const orig = store.originalRows[idx];
                    if (!orig) continue;
                    for (const f of fields) {
                        if (orig[f] !== undefined) {
                            store.updateCell(idx, f, orig[f] ?? '');
                        }
                    }
                }
            } else if (taskType === 'sku-weight') {
                // 恢复 sku 重量（按行）
                for (const idx of group.indices) {
                    const orig = store.originalRows[idx];
                    if (orig) store.updateCell(idx, 'skuWeight', orig.skuWeight ?? '');
                }
            }
        }

        let newTaskResult: StepTaskResult = { taskType, status: 'failed', message: '不支持的任务类型', durationMs: 0 };
        try {
            if (taskType === 'title') {
                newTaskResult = await executeTitleForProduct(group, abortController.signal);
            } else if (taskType === 'filter') {
                newTaskResult = await executeFilterForProduct(group, abortController.signal);
            } else if (taskType === 'size') {
                newTaskResult = await executeSizeForProduct(group, abortController.signal);
            } else if (taskType === 'sku-spec') {
                newTaskResult = await executeSkuSpecForProduct(group, abortController.signal);
            } else if (taskType === 'sku-weight') {
                newTaskResult = await executeSkuWeightForProduct(group, abortController.signal);
            }
        } catch (err) {
            newTaskResult = { taskType, status: 'failed', message: err instanceof Error ? err.message : String(err), durationMs: Date.now() - taskStart };
        }

        // 更新 completedResults 中对应产品的结果
        const updatedTaskResults = result.taskResults.map(r => r.taskType === taskType ? newTaskResult : r);
        const snapshotAfter: DataRow[] = group.indices.map(idx => ({ ...useDataStore.getState().rows[idx] }));
        const totalDurationMs = result.totalDurationMs + newTaskResult.durationMs;

        set((state) => ({
            stepState: {
                ...state.stepState,
                completedResults: state.stepState.completedResults.map(r =>
                    r.productId === productId
                        ? { ...r, taskResults: updatedTaskResults, snapshotAfter, totalDurationMs }
                        : r
                ),
                processing: false,
            },
        }));
    },

    cancelTask: (id) => {
        const task = get().tasks.get(id);
        if (task?.abortController) task.abortController.abort();
    },

    dismissTask: (id) => {
        set((state) => {
            const next = new Map(state.tasks);
            next.delete(id);
            return { tasks: next };
        });
    },
}));

// --- 辅助函数 ---

interface ProductGroup {
    productId: string;
    indices: number[];   // 该产品所有选中行的索引
    firstIndex: number;  // 第一行索引（用于读取公共字段）
}

function groupByProductId(indices: number[], rows: DataRow[]): ProductGroup[] {
    const map = new Map<string, ProductGroup>();
    for (const idx of indices) {
        const row = rows[idx];
        if (!row) continue;
        const pid = (row.productId ?? '').trim() || `__noid_${idx}__`;
        if (!map.has(pid)) {
            map.set(pid, { productId: pid, indices: [idx], firstIndex: idx });
        } else {
            map.get(pid)!.indices.push(idx);
        }
    }
    return Array.from(map.values());
}

// 将更新应用到同产品的所有行
function applyToGroup(group: ProductGroup, updates: Partial<DataRow>, store: ReturnType<typeof useDataStore.getState>) {
    for (const idx of group.indices) {
        store.updateRow(idx, updates);
    }
}

// --- 标题优化（按产品去重，含重试） ---

const MAX_RETRIES = 3;
const MAX_RETRY_DELAY_MS = 180_000; // 3 分钟

async function sleepWithAbort(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(resolve, ms);
        const onAbort = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
        if (signal.aborted) { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); return; }
        signal.addEventListener('abort', onAbort, { once: true });
    });
}

async function retryWithBackoff<T>(
    fn: () => Promise<T>,
    signal: AbortSignal,
    maxRetries: number = MAX_RETRIES,
): Promise<{ result: T | null; error: string | null }> {
    let lastError: string | null = null;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        if (signal.aborted) return { result: null, error: '已取消' };
        try {
            const result = await fn();
            return { result, error: null };
        } catch (err) {
            lastError = err instanceof Error ? err.message : String(err);
            if (attempt < maxRetries) {
                // 指数退避：2^attempt 秒，但不超过 3 分钟
                const delay = Math.min(1000 * Math.pow(2, attempt), MAX_RETRY_DELAY_MS);
                try {
                    await sleepWithAbort(delay, signal);
                } catch {
                    return { result: null, error: '已取消' };
                }
            }
        }
    }
    return { result: null, error: lastError };
}

// ===== SKU 规格优化 / 估重 批量执行（模块级） =====

// 从 LLM 返回文本中解析重量（Kg，保留 2 位小数）
function parseWeightText(text: string): string {
    const match = text.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
    if (!match) return '';
    const num = parseFloat(match[0]);
    if (isNaN(num)) return '';
    return num.toFixed(2);
}

// 收集 sku 估重需要参考的图片链接（最多 4 张，避免请求体过大）
async function collectSkuWeightImages(
    row: Record<string, unknown>,
    useImage: boolean,
): Promise<string[]> {
    if (!useImage) return [];
    const { parseImageUrls } = await import('@/services/excelService');
    return parseImageUrls(String(row.skuImage ?? '')).slice(0, 4);
}

// 调用 LLM 估算重量：提供了图片则走多模态接口，否则纯文本
async function estimateSkuWeight(
    instance: LLMInstance,
    model: string,
    prompt: string,
    context: string,
    images: string[],
): Promise<string> {
    const { optimizeTitle, callLLMWithImages } = await import('@/services/llmService');
    if (images.length > 0) {
        return callLLMWithImages(instance, model, prompt, context, images);
    }
    return optimizeTitle(instance, model, prompt, context);
}

// sku 估重时仅发送给模型的最小文本字段（SKU 图片通过多模态接口单独发送，避免整行数据发给大模型）
const SKU_WEIGHT_CONTEXT_KEYS = ['productName', 'skuSpec1', 'skuSpec2'];

// 将某一行 SKU 估重需要的字段拼为模型可读的内容（仅产品名称、规格1、规格2）
function buildSkuWeightContext(row: Record<string, unknown>): string {
    const lines: string[] = [];
    for (const key of SKU_WEIGHT_CONTEXT_KEYS) {
        const raw = row[key];
        const val = Array.isArray(raw) ? raw.join('，') : String(raw ?? '').trim();
        if (!val) continue;
        const label = EXCEL_FIELD_LABELS[key] || key;
        lines.push(`${label}：${val}`);
    }
    return lines.join('\n');
}

// 多轮估重：对单个 SKU 重复调用模型，返回多次有效结果的平均值（保留 2 位小数）；无任何有效结果返回 null
async function estimateMultiRoundWeight(
    instance: LLMInstance,
    model: string,
    prompt: string,
    context: string,
    images: string[],
    multiRound: boolean,
    repeatCount: number,
    signal: AbortSignal,
): Promise<string | null> {
    const rounds = multiRound && repeatCount > 1 ? repeatCount : 1;
    const values: number[] = [];
    for (let r = 0; r < rounds; r++) {
        if (signal.aborted) break;
        const { result, error } = await retryWithBackoff(
            () => estimateSkuWeight(instance, model, prompt, context, images),
            signal,
        );
        if (error || result == null) continue;
        const w = parseWeightText(result);
        if (w) values.push(parseFloat(w));
    }
    if (values.length === 0) return null;
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    return avg.toFixed(2);
}

async function runTitleSubmissionBatches(groups: ProductGroup[], config: ReturnType<typeof useConfigStore.getState>, store: ReturnType<typeof useDataStore.getState>, signal: AbortSignal, onProgress: (p: ProgressInfo) => void) {
    const candidates = groups.map(group => ({ group, id: group.productId, title: String(store.rows[group.firstIndex]?.productName ?? '').trim() }));
    const items = candidates.filter(item => item.title);
    const skipped = candidates.filter(item => !item.title);
    let done = 0;
    for (const item of skipped) {
        await markBatchProductCompleted('title', item.group.productId);
        done++;
    }
    if (items.length === 0) {
        onProgress({ current: done, total: groups.length, message: `完成：已跳过 ${done} 个空标题商品` });
        return;
    }
    const instance = config.llmInstances.find(item => item.id === config.titleConfig.llmInstanceId) ?? config.llmInstances[0];
    const model = config.titleConfig.model || instance?.models[0];
    if (!instance || !model) throw new Error('未配置 LLM 实例或模型');
    const mode = config.titleConfig.submissionMode;
    const size = configuredBatchSize(config.titleConfig.batchProductCount);
    const { callLLM } = await import('@/services/llmService');
    for (let offset = 0; offset < items.length && !signal.aborted; offset += size) {
        const batch = items.slice(offset, offset + size);
        onProgress({ current: done, total: groups.length, message: `正在提交第 ${Math.floor(offset / size) + 1} 批（${batch.length} 个商品）...` });
        const request = buildTitleBatchRequest(mode, config.titleConfig.prompt, batch.map(({ id, title }) => ({ id, title })));
        const { result, error } = await retryWithBackoff(() => callLLM(instance, model, request.system, request.user), signal);
        try {
            if (!result) throw new Error(error || '批量标题请求失败');
            const output = parseTitleBatchResponse(mode, result);
            const byId = new Map(output.map(item => [item.id, item.title]));
            if (byId.size !== batch.length || batch.some(item => !byId.has(item.id))) throw new Error(`模型返回结果不完整：应返回 ${batch.length} 项，实际有效 ${byId.size} 项`);
            for (const item of batch) store.updateCell(item.group.firstIndex, 'productName', byId.get(item.id)!);
        } catch (batchError) {
            for (const item of batch) for (const row of item.group.indices) store.setCellError(row, 'productName', batchError instanceof Error ? batchError.message : String(batchError));
        }
        for (const item of batch) await markBatchProductCompleted('title', item.group.productId);
        done += batch.length;
        onProgress({ current: done, total: groups.length, message: `已处理 ${done}/${groups.length} 个商品` });
        if (offset + batch.length < items.length) await new Promise(resolve => setTimeout(resolve, Math.max(0, config.titleConfig.requestInterval) * 1000));
    }
}

async function runSkuSpecSubmissionBatches(groups: ProductGroup[], config: ReturnType<typeof useConfigStore.getState>, store: ReturnType<typeof useDataStore.getState>, signal: AbortSignal, onProgress: (p: ProgressInfo) => void) {
    const fields = config.skuConfig.specFields ?? [];
    const instance = config.llmInstances.find(item => item.id === config.skuConfig.specLlmInstanceId) ?? config.llmInstances[0];
    const model = config.skuConfig.specModel || instance?.models[0];
    if (!instance || !model || fields.length === 0) throw new Error('未配置 SKU 规格优化字段或 LLM 实例/模型');
    const mode = config.skuConfig.specSubmissionMode;
    const candidates = groups.map(group => ({ group, productId: group.productId, rows: buildSkuRows(store.rows, group.indices, fields, config.skuConfig.specSkipShortEnabled, config.skuConfig.specSkipShortChars) }));
    const products = candidates.filter(product => product.rows.length > 0);
    const skipped = candidates.filter(product => product.rows.length === 0);
    const size = mode === 'batch-json' ? configuredBatchSize(config.skuConfig.specBatchProductCount) : 1;
    const { callLLM } = await import('@/services/llmService');
    let done = 0;
    for (const product of skipped) {
        await markBatchProductCompleted('sku-spec', product.group.productId);
        done++;
    }
    if (products.length === 0) {
        onProgress({ current: done, total: groups.length, message: `完成：已跳过 ${done} 个无需优化的商品` });
        return;
    }
    for (let offset = 0; offset < products.length && !signal.aborted; offset += size) {
        const batch = products.slice(offset, offset + size);
        const request = mode === 'batch-json'
            ? buildSkuProductsBatchRequest(config.skuConfig.specPrompt, batch, fields)
            : buildSkuProductRequest(mode, config.skuConfig.specPrompt, batch[0].productId, batch[0].rows, fields);
        const { result, error } = await retryWithBackoff(() => callLLM(instance, model, request.system, request.user), signal);
        try {
            if (!result) throw new Error(error || 'SKU 规格批量请求失败');
            const output = mode === 'batch-json'
                ? parseSkuProductsBatchResponse(result, fields)
                : [{ productId: batch[0].productId, skus: parseSkuProductResponse(mode, result, fields) }];
            const byProduct = new Map(output.map(product => [product.productId, product.skus]));
            for (const product of batch) {
                const rows = byProduct.get(product.productId) ?? [];
                const byRow = new Map(rows.map(row => [row.row, row.values]));
                if (rows.length !== product.rows.length || product.rows.some(row => !byRow.has(row.row))) throw new Error(`商品 ${product.productId} 返回的 SKU 结果不完整`);
                for (const row of product.rows) for (const [field, value] of Object.entries(byRow.get(row.row)!)) store.updateCell(row.row, field, value);
            }
        } catch (batchError) {
            for (const product of batch) for (const row of product.rows) for (const field of fields) store.setCellError(row.row, field, batchError instanceof Error ? batchError.message : String(batchError));
        }
        for (const product of batch) await markBatchProductCompleted('sku-spec', product.group.productId);
        done += batch.length;
        onProgress({ current: done, total: groups.length, message: `已处理 ${done}/${groups.length} 个商品` });
        if (offset + batch.length < products.length) await new Promise(resolve => setTimeout(resolve, Math.max(0, config.skuConfig.specRequestInterval) * 1000));
    }
}

async function runSkuWeightSubmissionBatches(groups: ProductGroup[], config: ReturnType<typeof useConfigStore.getState>, store: ReturnType<typeof useDataStore.getState>, signal: AbortSignal, onProgress: (p: ProgressInfo) => void) {
    const instance = config.llmInstances.find(item => item.id === config.skuConfig.weightLlmInstanceId) ?? config.llmInstances[0];
    const model = config.skuConfig.weightModel || instance?.models[0];
    if (!instance || !model) throw new Error('未配置 SKU 估重 LLM 实例/模型');
    const products: Array<{ group: ProductGroup; productId: string; productName: string; skus: SkuWeightBatchItem[] }> = [];
    for (const group of groups) {
        const skus: SkuWeightBatchItem[] = [];
        for (const row of group.indices) skus.push({ row, skuName: buildSkuWeightContext(store.rows[row] as Record<string, unknown>), skuImages: await collectSkuWeightImages(store.rows[row] as Record<string, unknown>, config.skuConfig.weightUseImage) });
        if (skus.length > 0) products.push({ group, productId: group.productId, productName: String(store.rows[group.firstIndex]?.productName ?? ''), skus });
    }
    const mode = config.skuConfig.weightSubmissionMode;
    const size = mode === 'batch-json' ? configuredBatchSize(config.skuConfig.weightBatchProductCount) : 1;
    const { callLLM, callLLMWithImages } = await import('@/services/llmService');
    let done = 0;
    for (let offset = 0; offset < products.length && !signal.aborted; offset += size) {
        const batch = products.slice(offset, offset + size);
        const request = mode === 'batch-json' ? buildSkuWeightProductsBatchRequest(config.skuConfig.weightPrompt, batch) : buildSkuWeightProductRequest(mode, config.skuConfig.weightPrompt, batch[0]);
        const images = config.skuConfig.weightUseImage ? Array.from(new Set(batch.flatMap(product => product.skus.flatMap(sku => sku.skuImages)))) : [];
        const rounds = config.skuConfig.weightMultiRound ? Math.max(1, config.skuConfig.weightRepeatCount) : 1;
        const values = new Map<string, number[]>();
        let batchError = '';
        for (let round = 0; round < rounds && !signal.aborted; round++) {
            const response = await retryWithBackoff(() => images.length > 0 ? callLLMWithImages(instance, model, request.system, request.user, images) : callLLM(instance, model, request.system, request.user), signal);
            if (!response.result) { batchError = response.error || 'SKU 估重批量请求失败'; continue; }
            try {
                const output = mode === 'batch-json' ? parseSkuWeightProductsBatchResponse(response.result) : [{ productId: batch[0].productId, skus: parseSkuWeightProductResponse(mode, response.result) }];
                for (const product of output) for (const sku of product.skus) {
                    const weight = parseWeightText(sku.weight);
                    if (!weight) continue;
                    const key = `${product.productId}\u0000${sku.row}`;
                    values.set(key, [...(values.get(key) ?? []), Number(weight)]);
                }
            } catch (error) { batchError = error instanceof Error ? error.message : String(error); }
        }
        for (const product of batch) for (const sku of product.skus) {
            const weights = values.get(`${product.productId}\u0000${sku.row}`) ?? [];
            if (weights.length > 0) store.updateCell(sku.row, 'skuWeight', (weights.reduce((sum, value) => sum + value, 0) / weights.length).toFixed(2));
            else store.setCellError(sku.row, 'skuWeight', batchError || '模型未返回有效重量');
        }
        for (const product of batch) await markBatchProductCompleted('sku-weight', product.group.productId);
        done += batch.length;
        onProgress({ current: done, total: products.length, message: `已处理 ${done}/${products.length} 个商品` });
        if (done < products.length) await new Promise(resolve => setTimeout(resolve, Math.max(0, config.skuConfig.weightRequestInterval) * 1000));
    }
}

async function runSkuSpecBatch(
    groups: ProductGroup[],
    config: ReturnType<typeof useConfigStore.getState>,
    store: ReturnType<typeof useDataStore.getState>,
    signal: AbortSignal,
    onProgress: (p: ProgressInfo) => void,
) {
    const fields = config.skuConfig.specFields ?? [];
    const instance = config.llmInstances.find(i => i.id === config.skuConfig.specLlmInstanceId) ?? config.llmInstances[0];
    const model = config.skuConfig.specModel || instance?.models[0];
    if (fields.length === 0 || !instance || !model) {
        onProgress({ current: 0, total: 0, message: '未配置 SKU 规格优化字段或 LLM 实例/模型' });
        logError('SKU规格优化', '未配置 SKU 规格优化字段或 LLM 实例/模型');
        return;
    }

    const { optimizeTitle } = await import('@/services/llmService');
    const prompt = config.skuConfig.specPrompt;
    logInfo('SKU规格优化', `使用 ${instance.name} / ${model}，共 ${groups.length} 个产品，字段: ${fields.join(', ')}`);

    type Unit = { idx: number; field: string; original: string };
    const units: Unit[] = [];
    for (const group of groups) {
        for (const idx of group.indices) {
            const row = store.rows[idx] as Record<string, unknown>;
            for (const field of fields) {
                const original = String(row[field] ?? '');
                if (original.trim()) units.push({ idx, field, original });
            }
        }
    }

    const total = units.length;
    if (total === 0) {
        onProgress({ current: 0, total: 0, message: '没有需要优化的 SKU 规格内容' });
        logWarn('SKU规格优化', '没有需要优化的 SKU 规格内容');
        return;
    }

    let success = 0;
    let failed = 0;
    for (let i = 0; i < total; i++) {
        if (signal.aborted) { onProgress({ current: i, total, message: '已取消' }); break; }
        const unit = units[i];
        onProgress({ current: i + 1, total, message: `正在优化第 ${i + 1}/${total} 个 SKU 规格单元（${EXCEL_FIELD_LABELS[unit.field] ?? unit.field}）...` });
        const { result, error } = await retryWithBackoff(
            () => optimizeTitle(instance, model, prompt, unit.original),
            signal,
        );
        if (result) {
            store.updateCell(unit.idx, unit.field, result);
            success++;
        } else if (error && error !== '已取消') {
            store.setCellError(unit.idx, unit.field, error);
            failed++;
        }
        if (i < total - 1) await new Promise(r => setTimeout(r, (config.skuConfig.specRequestInterval ?? 10) * 1000));
    }

    const parts = [`成功 ${success} 个`];
    if (failed > 0) parts.push(`失败 ${failed} 个`);
    onProgress({ current: total, total, message: `完成：${parts.join('，')}` });
    if (failed > 0) logWarn('SKU规格优化', `批量完成：${parts.join('，')}`);
    else logSuccess('SKU规格优化', `批量完成：${parts.join('，')}`);
}

async function runSkuWeightBatch(
    groups: ProductGroup[],
    config: ReturnType<typeof useConfigStore.getState>,
    store: ReturnType<typeof useDataStore.getState>,
    signal: AbortSignal,
    onProgress: (p: ProgressInfo) => void,
) {
    const instance = config.llmInstances.find(i => i.id === config.skuConfig.weightLlmInstanceId) ?? config.llmInstances[0];
    const model = config.skuConfig.weightModel || instance?.models[0];
    if (!instance || !model) {
        onProgress({ current: 0, total: 0, message: '未配置 SKU 估重 LLM 实例/模型' });
        logError('SKU估重', '未配置 SKU 估重 LLM 实例/模型');
        return;
    }

    const prompt = config.skuConfig.weightPrompt;
    const useImage = !!config.skuConfig.weightUseImage;
    const total = groups.reduce((s, g) => s + g.indices.length, 0);
    if (total === 0) { onProgress({ current: 0, total: 0, message: '没有需要估重的行' }); return; }

    let success = 0;
    let failed = 0;
    let done = 0;
    for (const group of groups) {
        if (signal.aborted) break;
        for (const idx of group.indices) {
            if (signal.aborted) break;
            const row = store.rows[idx] as Record<string, unknown>;
            const context = buildSkuWeightContext(row);
            const images = await collectSkuWeightImages(row, useImage);
            const { buildLLMCurlCommand } = await import('@/services/llmService');
            const curl = buildLLMCurlCommand(instance, model, prompt, context, images);
            logInfo(
                'SKU估重',
                `第 ${done + 1}/${total} 个 SKU 请求`,
                `实例: ${instance.name} / ${model}\n提示词: ${prompt}\n内容:\n${context}${images.length > 0 ? `\n图片(${images.length}张)` : ''}\n\n实际请求 (curl):\n${curl}`,
            );
            const rounds = config.skuConfig.weightMultiRound && (config.skuConfig.weightRepeatCount || 1) > 1 ? (config.skuConfig.weightRepeatCount || 1) : 1;
            onProgress({ current: done + 1, total, message: `正在估算第 ${done + 1}/${total} 个 SKU 重量${rounds > 1 ? `（多轮 ${rounds} 次取平均）` : ''}...` });
            const weightStr = await estimateMultiRoundWeight(instance, model, prompt, context, images, !!config.skuConfig.weightMultiRound, config.skuConfig.weightRepeatCount || 1, signal);
            if (weightStr != null) {
                store.updateCell(idx, 'skuWeight', weightStr);
                success++;
            } else {
                store.setCellError(idx, 'skuWeight', '多次估重均无有效结果');
                failed++;
            }
            done++;
            if (done < total) await new Promise(r => setTimeout(r, (config.skuConfig.weightRequestInterval ?? 10) * 1000));
        }
    }
    const parts = [`成功 ${success} 个`];
    if (failed > 0) parts.push(`失败 ${failed} 个`);
    onProgress({ current: total, total, message: `完成：${parts.join('，')}` });
    if (failed > 0) logWarn('SKU估重', `批量完成：${parts.join('，')}`);
    else logSuccess('SKU估重', `批量完成：${parts.join('，')}`);
}

async function executeSkuSpecForProduct(group: ProductGroup, signal: AbortSignal): Promise<StepTaskResult> {
    const config = useConfigStore.getState();
    const store = useDataStore.getState();
    const start = Date.now();
    const fields = config.skuConfig.specFields ?? [];
    const instance = config.llmInstances.find(i => i.id === config.skuConfig.specLlmInstanceId) ?? config.llmInstances[0];
    const model = config.skuConfig.specModel || instance?.models[0];
    if (fields.length === 0 || !instance || !model) {
        return { taskType: 'sku-spec', status: 'skipped', message: '未配置字段或 LLM 实例/模型', durationMs: Date.now() - start };
    }
    const { optimizeTitle } = await import('@/services/llmService');
    const prompt = config.skuConfig.specPrompt;
    let changed = 0;
    let failed = 0;
    const units = group.indices.flatMap(idx => fields
        .map(field => ({ idx, field, original: String((store.rows[idx] as Record<string, unknown>)[field] ?? '') }))
        .filter(unit => unit.original.trim()));
    for (let unitIndex = 0; unitIndex < units.length; unitIndex++) {
        if (signal.aborted) break;
        const unit = units[unitIndex];
        const { result, error } = await retryWithBackoff(
            () => optimizeTitle(instance, model, prompt, unit.original),
            signal,
        );
        if (result) {
            store.updateCell(unit.idx, unit.field, result);
            changed++;
        } else if (error && error !== '已取消') {
            store.setCellError(unit.idx, unit.field, error);
            failed++;
        }
        if (unitIndex < units.length - 1 && !signal.aborted) await new Promise(resolve => setTimeout(resolve, (config.skuConfig.specRequestInterval ?? 10) * 1000));
    }
    if (changed === 0 && failed === 0) {
        return { taskType: 'sku-spec', status: 'skipped', message: '没有需要优化的内容', durationMs: Date.now() - start };
    }
    let status: 'success' | 'failed' = 'success';
    if (failed > 0 && changed === 0) status = 'failed';
    const msg = failed > 0 ? `优化 ${changed} 个，失败 ${failed} 个` : `优化 ${changed} 个 SKU 规格`;
    return { taskType: 'sku-spec', status, message: msg, durationMs: Date.now() - start };
}

async function executeSkuWeightForProduct(group: ProductGroup, signal: AbortSignal): Promise<StepTaskResult> {
    const config = useConfigStore.getState();
    const store = useDataStore.getState();
    const start = Date.now();
    const instance = config.llmInstances.find(i => i.id === config.skuConfig.weightLlmInstanceId) ?? config.llmInstances[0];
    const model = config.skuConfig.weightModel || instance?.models[0];
    if (!instance || !model) {
        return { taskType: 'sku-weight', status: 'skipped', message: '未配置 LLM 实例/模型', durationMs: Date.now() - start };
    }
    const prompt = config.skuConfig.weightPrompt;
    const useImage = !!config.skuConfig.weightUseImage;
    let changed = 0;
    let failed = 0;
    for (let rowPosition = 0; rowPosition < group.indices.length; rowPosition++) {
        if (signal.aborted) break;
        const idx = group.indices[rowPosition];
        const row = store.rows[idx] as Record<string, unknown>;
        const context = buildSkuWeightContext(row);
        const images = await collectSkuWeightImages(row, useImage);
        const { buildLLMCurlCommand } = await import('@/services/llmService');
        const curl = buildLLMCurlCommand(instance, model, prompt, context, images);
        logInfo(
            'SKU估重',
            `第 ${idx + 1} 个 SKU 请求`,
            `实例: ${instance.name} / ${model}\n提示词: ${prompt}\n内容:\n${context}${images.length > 0 ? `\n图片(${images.length}张)` : ''}\n\n实际请求 (curl):\n${curl}`,
        );
        const weightStr = await estimateMultiRoundWeight(instance, model, prompt, context, images, !!config.skuConfig.weightMultiRound, config.skuConfig.weightRepeatCount || 1, signal);
        if (weightStr != null) {
            store.updateCell(idx, 'skuWeight', weightStr);
            changed++;
        } else {
            store.setCellError(idx, 'skuWeight', '多次估重均无有效结果');
            failed++;
        }
        if (rowPosition < group.indices.length - 1 && !signal.aborted) await new Promise(resolve => setTimeout(resolve, (config.skuConfig.weightRequestInterval ?? 10) * 1000));
    }
    if (changed === 0 && failed === 0) {
        return { taskType: 'sku-weight', status: 'skipped', message: '没有可估算的行', durationMs: Date.now() - start };
    }
    let status: 'success' | 'failed' = 'success';
    if (failed > 0 && changed === 0) status = 'failed';
    const msg = failed > 0 ? `完成 ${changed} 个，失败 ${failed} 个` : `完成 ${changed} 个 SKU 估重`;
    return { taskType: 'sku-weight', status, message: msg, durationMs: Date.now() - start };
}

async function runTitleBatch(
    groups: ProductGroup[],
    config: ReturnType<typeof useConfigStore.getState>,
    store: ReturnType<typeof useDataStore.getState>,
    signal: AbortSignal,
    onProgress: (p: ProgressInfo) => void,
) {
    const instance = config.llmInstances.find(i => i.id === config.titleConfig.llmInstanceId) ?? config.llmInstances[0];
    const model = config.titleConfig.model || instance?.models[0];
    if (!instance || !model) {
        onProgress({ current: 0, total: 0, message: '未配置 LLM 实例或模型' });
        logError('优化标题', '未配置 LLM 实例或模型');
        return;
    }

    const { optimizeTitle } = await import('@/services/llmService');
    logInfo('优化标题', `使用 ${instance.name} / ${model}，共 ${groups.length} 个产品`);
    const total = groups.length;
    let success = 0;
    let failed = 0;

    for (let i = 0; i < total; i++) {
        if (signal.aborted) { onProgress({ current: i, total, message: '已取消' }); break; }

        const group = groups[i];
        const original = store.rows[group.firstIndex]?.productName ?? '';
        onProgress({ current: i + 1, total, message: `正在优化第 ${i + 1}/${total} 个产品（${group.indices.length} 个 SKU）...` });

        if (original.trim()) {
            const { result, error } = await retryWithBackoff(
                () => optimizeTitle(instance, model, config.titleConfig.prompt, original),
                signal,
            );
            if (result) {
                store.updateCell(group.firstIndex, 'productName', result);
                success++;
                logSuccess('优化标题', `产品 ${group.productId} 优化成功`, `原始: ${original}\n优化后: ${result}`);
            } else if (error && error !== '已取消') {
                // 记录错误到该产品的所有行
                for (const idx of group.indices) {
                    store.setCellError(idx, 'productName', error);
                }
                failed++;
                logError('优化标题', `产品 ${group.productId} 优化失败: ${error}`, `原始标题: ${original}`);
            }
        }

        if (i < total - 1) await new Promise(r => setTimeout(r, (config.titleConfig.requestInterval ?? 10) * 1000));
    }
    const parts = [`成功 ${success} 个`];
    if (failed > 0) parts.push(`失败 ${failed} 个`);
    onProgress({ current: total, total, message: `完成：${parts.join('，')}` });
    if (failed > 0) {
        logWarn('优化标题', `批量优化完成：${parts.join('，')}`);
    } else {
        logSuccess('优化标题', `批量优化完成：${parts.join('，')}`);
    }
}

// --- 低质量过滤（公共字段按产品去重，SKU 字段逐行处理） ---

async function runFilterBatch(
    groups: ProductGroup[],
    config: ReturnType<typeof useConfigStore.getState>,
    store: ReturnType<typeof useDataStore.getState>,
    signal: AbortSignal,
    onProgress: (p: ProgressInfo) => void,
) {
    if (!selectOCRInstance(config.ocrInstances)) {
        onProgress({ current: 0, total: 0, message: '无可用 OCR 实例' });
        logError('过滤低质量', '无可用 OCR 实例');
        return;
    }

    const { callOCR, containsAnyKeyword } = await import('@/services/ocrService');
    const { getCachedOCRResult, setCachedOCRResult } = await import('@/services/ocrCache');
    const { parseImageUrls, joinImageUrls } = await import('@/services/excelService');

    const lowQualityConfig = config.lowQualityConfig;
    const targetFields: string[] = [];
    if (lowQualityConfig.filterDetailImages) targetFields.push('detailImages');
    if (lowQualityConfig.filterMainImages) targetFields.push('mainImages');
    if (lowQualityConfig.filterSkuImages) targetFields.push('skuImage');
    if (targetFields.length === 0) {
        onProgress({ current: 0, total: 0, message: '未勾选任何过滤目标' });
        logWarn('过滤低质量', '未勾选任何过滤目标');
        return;
    }

    logInfo('过滤低质量', `过滤目标: ${targetFields.join(', ')}，关键词: ${lowQualityConfig.keywords.join(', ')}`);

    // 收集需要 OCR 的图片任务（去重：公共字段按产品只取第一行）
    // url -> { groupIndices: number[], fieldKey: string }
    const urlTasks: { url: string; fieldKey: string; isCommon: boolean; group: ProductGroup }[] = [];
    const seenCommonUrls = new Set<string>(); // 公共字段 URL 去重

    for (const group of groups) {
        for (const fieldKey of targetFields) {
            const isCommon = PRODUCT_COMMON_IMAGE_FIELDS.includes(fieldKey);
            if (isCommon) {
                // 公共字段：只处理第一行
                const firstRow = store.rows[group.firstIndex];
                const urls = parseImageUrls(firstRow?.[fieldKey] ?? '');
                for (const url of urls) {
                    if (!seenCommonUrls.has(url)) {
                        seenCommonUrls.add(url);
                        urlTasks.push({ url, fieldKey, isCommon: true, group });
                    }
                }
            } else {
                // SKU 字段：逐行处理
                for (const idx of group.indices) {
                    const row = store.rows[idx];
                    const urls = parseImageUrls(row?.[fieldKey] ?? '');
                    for (const url of urls) {
                        urlTasks.push({ url, fieldKey, isCommon: false, group });
                    }
                }
            }
        }
    }

    const total = urlTasks.length;
    if (total === 0) {
        onProgress({ current: 0, total: 0, message: '没有需要处理的图片' });
        return;
    }

    let processed = 0;
    let removed = 0;
    // 记录每个 (产品, 字段) 要移除的 URL
    // key: `${group.firstIndex}-${fieldKey}` -> Set<url>
    const removals = new Map<string, { group: ProductGroup; fieldKey: string; urls: Set<string> }>();

    for (const task of urlTasks) {
        if (signal.aborted) { onProgress({ current: processed, total, message: '已取消' }); break; }

        processed++;
        onProgress({ current: processed, total, message: `正在识别第 ${processed}/${total} 张图片...` });

        const instance = selectOCRInstance(useConfigStore.getState().ocrInstances);
        if (!instance) {
            onProgress({ current: processed, total, message: `无可用 OCR 实例，已停止（已处理 ${processed}/${total}）` });
            break;
        }

        let ocrText = getCachedOCRResult(task.url);
        if (!ocrText) {
            const res = await callOCR(instance, task.url);
            if (res.success && res.text) {
                ocrText = res.text;
                setCachedOCRResult(task.url, res.text);
            } else {
                logWarn('过滤低质量', `OCR 识别失败: ${res.error ?? '无结果'}`, `URL: ${task.url}`);
            }
        }
        if (ocrText) {
            const hits = containsAnyKeyword(ocrText, lowQualityConfig.keywords);
            if (hits.length > 0) {
                const key = `${task.group.firstIndex}-${task.fieldKey}`;
                if (!removals.has(key)) {
                    removals.set(key, { group: task.group, fieldKey: task.fieldKey, urls: new Set() });
                }
                removals.get(key)!.urls.add(task.url);
                removed++;
                logWarn('过滤低质量', `命中关键词 [${hits.join(', ')}]，标记移除`, `URL: ${task.url}\nOCR文本: ${ocrText}`);
            }
        }

        if (processed < total && !isLocalOCRInstance(instance)) await new Promise(r => setTimeout(r, (useConfigStore.getState().ocrSettings.requestInterval ?? 10) * 1000));
    }

    // 应用移除 + 删除 SKU 行
    const rowsToDelete: number[] = [];
    for (const { group, fieldKey, urls: urlsToRemove } of removals.values()) {
        if (PRODUCT_COMMON_IMAGE_FIELDS.includes(fieldKey)) {
            // 公共字段：读取第一行，过滤后应用到该产品所有行
            const firstRow = store.rows[group.firstIndex];
            const originalUrls = parseImageUrls(firstRow?.[fieldKey] ?? '');
            const filtered = originalUrls.filter(u => !urlsToRemove.has(u));
            applyToGroup(group, { [fieldKey]: joinImageUrls(filtered) }, store);
        } else {
            // SKU 字段：逐行处理
            for (const idx of group.indices) {
                const row = store.rows[idx];
                const originalUrls = parseImageUrls(row?.[fieldKey] ?? '');
                const filtered = originalUrls.filter(u => !urlsToRemove.has(u));
                if (filtered.length !== originalUrls.length) {
                    store.updateRow(idx, { [fieldKey]: joinImageUrls(filtered) });
                    if (fieldKey === 'skuImage' && filtered.length === 0 && lowQualityConfig.removeSkuRowIfEmpty) {
                        rowsToDelete.push(idx);
                    }
                }
            }
        }
    }

    if (rowsToDelete.length > 0) store.markRowsFiltered(rowsToDelete);
    const parts = [`移除 ${removed} 张图片`];
    if (rowsToDelete.length > 0) parts.push(`标记 ${rowsToDelete.length} 个 SKU 行为过滤`);
    onProgress({ current: total, total, message: `完成：${parts.join('，')}` });
    logSuccess('过滤低质量', `完成：${parts.join('，')}`);
}

// --- 尺码图识别（按产品去重，识别到后应用到同产品所有行） ---

async function runSizeBatch(
    groups: ProductGroup[],
    config: ReturnType<typeof useConfigStore.getState>,
    store: ReturnType<typeof useDataStore.getState>,
    signal: AbortSignal,
    onProgress: (p: ProgressInfo) => void,
) {
    if (!selectOCRInstance(config.ocrInstances)) {
        onProgress({ current: 0, total: 0, message: '无可用 OCR 实例' });
        logError('识别尺码图', '无可用 OCR 实例');
        return;
    }

    const { callOCR, containsAnyKeyword } = await import('@/services/ocrService');
    const { getCachedOCRResult, setCachedOCRResult } = await import('@/services/ocrCache');
    const { parseImageUrls } = await import('@/services/excelService');

    logInfo('识别尺码图', `共 ${groups.length} 个产品，关键词: ${config.sizeChartConfig.keywords.join(', ')}`);

    // 收集每个产品的详情图（去重 URL）
    const productTasks: { group: ProductGroup; urls: string[] }[] = [];
    const seenUrls = new Set<string>();

    for (const group of groups) {
        const firstRow = store.rows[group.firstIndex];
        const urls = parseImageUrls(firstRow?.detailImages ?? '');
        const uniqueUrls = urls.filter(u => {
            if (seenUrls.has(u)) return false;
            seenUrls.add(u);
            return true;
        });
        if (uniqueUrls.length > 0) {
            productTasks.push({ group, urls: uniqueUrls });
        }
    }

    const total = productTasks.reduce((s, t) => s + t.urls.length, 0);
    if (total === 0) {
        onProgress({ current: 0, total: 0, message: '没有需要处理的详情图' });
        return;
    }

    let processed = 0;
    let found = 0;

    for (const { group, urls } of productTasks) {
        let sizeChartFound = false;
        for (const url of urls) {
            if (signal.aborted) { onProgress({ current: processed, total, message: '已取消' }); break; }
            processed++;
            if (sizeChartFound) {
                onProgress({ current: processed, total, message: `跳过已识别产品 ${processed}/${total}` });
                continue;
            }

            onProgress({ current: processed, total, message: `正在识别第 ${processed}/${total} 张图片...` });

            const instance = selectOCRInstance(useConfigStore.getState().ocrInstances);
            if (!instance) {
                onProgress({ current: processed, total, message: `无可用 OCR 实例，已停止（已处理 ${processed}/${total}）` });
                return;
            }

            let ocrText = getCachedOCRResult(url);
            if (!ocrText) {
                const res = await callOCR(instance, url);
                if (res.success && res.text) {
                    ocrText = res.text;
                    setCachedOCRResult(url, res.text);
                }
            }
            if (ocrText && containsAnyKeyword(ocrText, config.sizeChartConfig.keywords).length > 0) {
                // 应用到同产品的所有行
                applyToGroup(group, { sizeChart: url }, store);
                sizeChartFound = true;
                found++;
            }

            if (processed < total && !isLocalOCRInstance(instance)) await new Promise(r => setTimeout(r, (useConfigStore.getState().ocrSettings.requestInterval ?? 10) * 1000));
        }
        if (signal.aborted) break;
    }

    onProgress({ current: total, total, message: `完成：识别到 ${found} 张尺码图` });
    logSuccess('识别尺码图', `完成：识别到 ${found} 张尺码图`);
}

// --- 步进模式：单产品执行函数 ---

function findProductGroup(productId: string, rows: DataRow[]): ProductGroup | null {
    const map = new Map<string, ProductGroup>();
    rows.forEach((row, idx) => {
        const pid = (row.productId ?? '').trim() || `__noid_${idx}__`;
        if (!map.has(pid)) {
            map.set(pid, { productId: pid, indices: [idx], firstIndex: idx });
        } else {
            map.get(pid)!.indices.push(idx);
        }
    });
    return map.get(productId) ?? null;
}

async function executeTitleForProduct(group: ProductGroup, signal: AbortSignal): Promise<StepTaskResult> {
    const config = useConfigStore.getState();
    const store = useDataStore.getState();
    const start = Date.now();

    const instance = config.llmInstances.find(i => i.id === config.titleConfig.llmInstanceId) ?? config.llmInstances[0];
    const model = config.titleConfig.model || instance?.models[0];
    if (!instance || !model) {
        return { taskType: 'title', status: 'skipped', message: '未配置 LLM 实例或模型', durationMs: Date.now() - start };
    }

    const original = store.rows[group.firstIndex]?.productName ?? '';
    if (!original.trim()) {
        return { taskType: 'title', status: 'skipped', message: '标题为空，跳过', durationMs: Date.now() - start };
    }

    const { optimizeTitle } = await import('@/services/llmService');
    const { result, error } = await retryWithBackoff(
        () => optimizeTitle(instance, model, config.titleConfig.prompt, original),
        signal,
    );

    if (result) {
        store.updateCell(group.firstIndex, 'productName', result);
        logSuccess('优化标题', `产品 ${group.productId} 优化成功`, `原始: ${original}\n优化后: ${result}`);
        return { taskType: 'title', status: 'success', message: `优化成功`, durationMs: Date.now() - start };
    } else {
        if (error && error !== '已取消') {
            for (const idx of group.indices) {
                store.setCellError(idx, 'productName', error);
            }
            logError('优化标题', `产品 ${group.productId} 优化失败: ${error}`);
        }
        return { taskType: 'title', status: 'failed', message: error ?? '未知错误', durationMs: Date.now() - start };
    }
}

async function executeFilterForProduct(group: ProductGroup, signal: AbortSignal): Promise<StepTaskResult> {
    const config = useConfigStore.getState();
    const store = useDataStore.getState();
    const start = Date.now();

    if (!selectOCRInstance(config.ocrInstances)) {
        return { taskType: 'filter', status: 'skipped', message: '无可用 OCR 实例', durationMs: Date.now() - start };
    }

    const { callOCR, containsAnyKeyword } = await import('@/services/ocrService');
    const { getCachedOCRResult, setCachedOCRResult } = await import('@/services/ocrCache');
    const { parseImageUrls, joinImageUrls } = await import('@/services/excelService');

    const lowQualityConfig = config.lowQualityConfig;
    const targetFields: string[] = [];
    if (lowQualityConfig.filterDetailImages) targetFields.push('detailImages');
    if (lowQualityConfig.filterMainImages) targetFields.push('mainImages');
    if (lowQualityConfig.filterSkuImages) targetFields.push('skuImage');
    if (targetFields.length === 0) {
        return { taskType: 'filter', status: 'skipped', message: '未勾选任何过滤目标', durationMs: Date.now() - start };
    }

    // 收集该产品的图片任务
    const urlTasks: { url: string; fieldKey: string; isCommon: boolean }[] = [];
    for (const fieldKey of targetFields) {
        const isCommon = PRODUCT_COMMON_IMAGE_FIELDS.includes(fieldKey);
        if (isCommon) {
            const firstRow = store.rows[group.firstIndex];
            const urls = parseImageUrls(firstRow?.[fieldKey] ?? '');
            for (const url of urls) {
                urlTasks.push({ url, fieldKey, isCommon: true });
            }
        } else {
            for (const idx of group.indices) {
                const row = store.rows[idx];
                const urls = parseImageUrls(row?.[fieldKey] ?? '');
                for (const url of urls) {
                    urlTasks.push({ url, fieldKey, isCommon: false });
                }
            }
        }
    }

    if (urlTasks.length === 0) {
        return { taskType: 'filter', status: 'skipped', message: '没有需要处理的图片', durationMs: Date.now() - start };
    }

    let removed = 0;
    const removals = new Map<string, { fieldKey: string; urls: Set<string> }>();

    for (let i = 0; i < urlTasks.length; i++) {
        if (signal.aborted) break;
        const task = urlTasks[i];

        const instance = selectOCRInstance(useConfigStore.getState().ocrInstances);
        if (!instance) break;

        let ocrText = getCachedOCRResult(task.url);
        if (!ocrText) {
            const res = await callOCR(instance, task.url);
            if (res.success && res.text) {
                ocrText = res.text;
                setCachedOCRResult(task.url, res.text);
            }
        }
        if (ocrText) {
            const hits = containsAnyKeyword(ocrText, lowQualityConfig.keywords);
            if (hits.length > 0) {
                const key = `${group.firstIndex}-${task.fieldKey}`;
                if (!removals.has(key)) {
                    removals.set(key, { fieldKey: task.fieldKey, urls: new Set() });
                }
                removals.get(key)!.urls.add(task.url);
                removed++;
            }
        }

        if (i < urlTasks.length - 1 && !isLocalOCRInstance(instance)) await new Promise(r => setTimeout(r, (useConfigStore.getState().ocrSettings.requestInterval ?? 10) * 1000));
    }

    // 应用移除
    const rowsToDelete: number[] = [];
    for (const { fieldKey, urls: urlsToRemove } of removals.values()) {
        if (PRODUCT_COMMON_IMAGE_FIELDS.includes(fieldKey)) {
            const firstRow = store.rows[group.firstIndex];
            const originalUrls = parseImageUrls(firstRow?.[fieldKey] ?? '');
            const filtered = originalUrls.filter(u => !urlsToRemove.has(u));
            applyToGroup(group, { [fieldKey]: joinImageUrls(filtered) }, store);
        } else {
            for (const idx of group.indices) {
                const row = store.rows[idx];
                const originalUrls = parseImageUrls(row?.[fieldKey] ?? '');
                const filtered = originalUrls.filter(u => !urlsToRemove.has(u));
                if (filtered.length !== originalUrls.length) {
                    store.updateRow(idx, { [fieldKey]: joinImageUrls(filtered) });
                    if (fieldKey === 'skuImage' && filtered.length === 0 && lowQualityConfig.removeSkuRowIfEmpty) {
                        rowsToDelete.push(idx);
                    }
                }
            }
        }
    }
    if (rowsToDelete.length > 0) store.markRowsFiltered(rowsToDelete);

    return { taskType: 'filter', status: 'success', message: `移除 ${removed} 张低质量图片`, durationMs: Date.now() - start };
}

async function executeSizeForProduct(group: ProductGroup, signal: AbortSignal): Promise<StepTaskResult> {
    const config = useConfigStore.getState();
    const store = useDataStore.getState();
    const start = Date.now();

    if (!selectOCRInstance(config.ocrInstances)) {
        return { taskType: 'size', status: 'skipped', message: '无可用 OCR 实例', durationMs: Date.now() - start };
    }

    const { callOCR, containsAnyKeyword } = await import('@/services/ocrService');
    const { getCachedOCRResult, setCachedOCRResult } = await import('@/services/ocrCache');
    const { parseImageUrls } = await import('@/services/excelService');

    const firstRow = store.rows[group.firstIndex];
    const urls = parseImageUrls(firstRow?.detailImages ?? '');
    if (urls.length === 0) {
        return { taskType: 'size', status: 'skipped', message: '没有详情图', durationMs: Date.now() - start };
    }

    let found = false;
    for (let i = 0; i < urls.length; i++) {
        if (signal.aborted || found) break;
        const url = urls[i];

        const instance = selectOCRInstance(useConfigStore.getState().ocrInstances);
        if (!instance) break;

        let ocrText = getCachedOCRResult(url);
        if (!ocrText) {
            const res = await callOCR(instance, url);
            if (res.success && res.text) {
                ocrText = res.text;
                setCachedOCRResult(url, res.text);
            }
        }
        if (ocrText && containsAnyKeyword(ocrText, config.sizeChartConfig.keywords).length > 0) {
            applyToGroup(group, { sizeChart: url }, store);
            found = true;
        }

        if (i < urls.length - 1 && !found && !isLocalOCRInstance(instance)) await new Promise(r => setTimeout(r, (useConfigStore.getState().ocrSettings.requestInterval ?? 10) * 1000));
    }

    if (found) {
        return { taskType: 'size', status: 'success', message: '识别到尺码图', durationMs: Date.now() - start };
    } else {
        return { taskType: 'size', status: 'success', message: '未识别到尺码图', durationMs: Date.now() - start };
    }
}
